import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DashboardService } from '../../services/dashboard.service';
import { LanguageService } from '../../services/language.service';
import { TenantFeaturesService } from '../../services/tenant-features.service';
import { DashboardOverview, DashboardLecture, DashboardLesson } from '../../models/dashboard';
import { ChildCardDto } from '../../models/parent-portal';
import { localizedMonth, localizedName, localizedWeekday } from '../../common/i18n/i18n-helpers';
import type { EChartsOption } from 'echarts';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent implements OnInit, OnDestroy {
  loading = true;
  loadFailed = false;
  data?: DashboardOverview;

  /** ECharts options (staff overview). Null until data arrives. */
  enrollmentOption: EChartsOption | null = null;
  gradeOption: EChartsOption | null = null;

  /** Weekday tabs for the upcoming-lectures panel. */
  readonly days = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  activeDay = 'Saturday';

  private readonly palette = ['#3b82f6', '#5b6fde', '#7c3aed', '#0e7490', '#16a34a', '#d97706'];

  private sub = new Subscription();

  constructor(
    private dash: DashboardService,
    private auth: AuthService,
    private translate: TranslateService,
    private language: LanguageService,
    private features: TenantFeaturesService,
  ) {}

  ngOnInit(): void {
    this.load();
    // ECharts options hold plain strings (month labels, series names), so rebuild them
    // once the new language's translations have loaded.
    this.sub.add(this.translate.onLangChange.subscribe(() => {
      if (this.data && this.hasCharts) this.buildCharts();
    }));
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.dash.getOverview().subscribe({
      next: (res) => {
        if (res.isSuccess && res.value) {
          this.data = res.value;
          this.activeDay = this.data.upcomingLectures[0]?.day ?? 'Saturday';
          if (this.hasCharts) this.buildCharts();
        } else {
          this.loadFailed = true;
        }
        this.loading = false;
      },
      error: () => { this.loading = false; this.loadFailed = true; },
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** A person or grade name in the UI language, falling back to whatever the API sent. */
  localized(en?: string | null, ar?: string | null, fallback?: string | null): string {
    return localizedName(this.language.current, en, ar) || fallback || '';
  }

  /** Weekday tab label in the UI language (the API sends English day names). */
  dayLabel(day: string): string {
    return localizedWeekday(day, this.language.current);
  }

  /** Which variant the API built (staff = Administrator/TenantAdmin, tenant-wide). */
  get isStaff(): boolean { return this.data?.role === 'staff'; }
  get isTeacher(): boolean { return this.data?.role === 'teacher'; }
  get isParent(): boolean { return this.data?.role === 'parent'; }
  get isStudent(): boolean { return this.data?.role === 'student'; }
  /** Charts are for the staff and teacher views only. */
  get hasCharts(): boolean { return this.isStaff || this.isTeacher; }
  get isTenantAdmin(): boolean { return this.auth.hasAnyRole('Administrator', 'TenantAdmin'); }
  get hasTutoring(): boolean { return this.features.hasModule('tutoring'); }

  /** Sum of the staff action items (the strip hides when nothing needs attention). */
  get actionTotal(): number {
    const a = this.data?.actionItems;
    return a ? a.pendingUserApprovals + a.pendingBookings + a.lessonsToday + a.ungradedSubmissions : 0;
  }

  childName(c: ChildCardDto): string { return this.localized(c.nameEn, c.nameAr); }
  childGrade(c: ChildCardDto): string { return this.localized(c.gradeNameEn, c.gradeNameAr); }

  lessonWith(l: DashboardLesson): string { return this.localized(l.withNameEn, l.withNameAr); }

  lessonTeacher(c: ChildCardDto): string {
    return c.nextLesson ? this.localized(c.nextLesson.teacherNameEn, c.nextLesson.teacherNameAr) : '';
  }

  trackChild(_: number, c: ChildCardDto): string { return c.childId; }
  trackLesson(_: number, l: DashboardLesson): string { return l.id; }

  /** Real month-over-month change in total students (from the cumulative trend). */
  get studentsDelta(): number | null {
    const t = this.data?.enrollmentTrend ?? [];
    if (t.length < 2) return null;
    const prev = t[t.length - 2].students;
    const last = t[t.length - 1].students;
    if (prev <= 0) return null;
    return Math.round(((last - prev) / prev) * 100);
  }

  /** Days that actually have an upcoming lecture (for the tab strip). */
  get activeDays(): string[] {
    const set = new Set(this.data?.upcomingLectures.map((l) => l.day));
    return this.days.filter((d) => set.has(d));
  }

  lecturesForDay(day: string): DashboardLecture[] {
    return this.data?.upcomingLectures.filter((l) => l.day === day) ?? [];
  }

  ratePct(v?: number): number { return Math.round((v ?? 0) * 100); }
  round(v?: number): number { return Math.round(v ?? 0); }

  private buildCharts(): void {
    const d = this.data!;
    // Arabic reads right-to-left, so the time axis runs the other way and the value axis swaps side.
    const rtl = this.language.isRtl;

    // Cumulative student enrollment — area chart.
    this.enrollmentOption = {
      grid: { top: 16, right: 18, bottom: 28, left: 44, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: d.enrollmentTrend.map((p) => localizedMonth(p.month, this.language.current)),
        boundaryGap: false,
        inverse: rtl,
        axisLine: { lineStyle: { color: '#dce4f7' } },
        axisTick: { show: false },
        axisLabel: { color: '#4a5f8a' },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        position: rtl ? 'right' : 'left',
        splitLine: { lineStyle: { color: '#eef2fb' } },
        axisLabel: { color: '#4a5f8a' },
      },
      series: [{
        name: this.translate.instant('dashboard.Students'),
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: d.enrollmentTrend.map((p) => p.students),
        lineStyle: { color: '#3b82f6', width: 3 },
        itemStyle: { color: '#3b82f6' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59,110,246,0.28)' },
              { offset: 1, color: 'rgba(59,110,246,0.02)' },
            ],
          },
        },
      }],
    };

    // Students by grade — donut.
    this.gradeOption = {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, icon: 'circle', textStyle: { color: '#4a5f8a' } },
      series: [{
        type: 'pie',
        radius: ['55%', '78%'],
        center: ['50%', '42%'],
        avoidLabelOverlap: false,
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: d.gradeDistribution.map((g, i) => ({
          name: localizedName(this.language.current, g.gradeNameEn, g.gradeNameAr) || g.gradeName,
          value: g.studentCount,
          itemStyle: { color: this.palette[i % this.palette.length] },
        })),
      }],
    };
  }
}
